import { Button } from '@utima/ui';
import { Component, type ErrorInfo, type ReactNode } from 'react';

import { AppSettings } from '@/constants/AppSettings';
import { captureError } from '@/services/analytics';

import type { Error } from './error/Error';

interface Props {
  children: ReactNode;
  fallback?: ReactNode;
}

interface State {
  hasError: boolean;
  error?: Error;
}

export class ErrorBoundary extends Component<Props, State> {
  constructor(props: Props) {
    super(props);
    this.state = { hasError: false };
  }

  static getDerivedStateFromError(error: Error): State {
    return { hasError: true, error };
  }

  componentDidCatch(error: Error, errorInfo: ErrorInfo) {
    // Report error to PostHog
    captureError(error, errorInfo);

    // Log to console in development
    if (import.meta.env.DEV || AppSettings.debug) {
      console.error('Error caught by boundary:', error, errorInfo);
    }
  }

  render() {
    if (this.state.hasError) {
      return (
        this.props.fallback || (
          <div className='mx-auto flex h-screen max-w-screen-xl items-center justify-center px-4 py-8 lg:px-6 lg:py-16'>
            <div className='mx-auto max-w-screen-sm text-center'>
              <h1 className='mb-4 text-7xl font-extrabold tracking-tight text-secondary lg:text-9xl'>
                Error
              </h1>
              <p className='mb-4 text-3xl font-bold tracking-tight'>
                We are sorry
              </p>
              <p className='mb-4 text-lg font-light'>Something went wrong.</p>
              <Button
                data-cta='admin-error-boundary-back-to-homepage'
                variant='primary'
                asChild
              >
                <a href='/'>Back to home</a>
              </Button>
            </div>
          </div>
        )
      );
    }

    return this.props.children;
  }
}
